<script>
import Layout from '@layouts/main'
import PlaylistsTableBlock from '@components/playlists/playlists-table-block'
import PlaylistInfo from '@components/playlists/playlist-info'
import DataLayer from '@services/data-layer/index'
import modalService from '@/services/modal-service'

export default {
    page() {
        const title = this.currentChart ? `${this.currentChart.revisions_title} - Chart` : 'Chart'
        const description = (this.currentChart && this.currentChart.revisions_description) || 'A chart on FRISKY'
        const image = (this.currentChart && this.currentChart.revisions_album_art && this.currentChart.revisions_album_art.url)
            || `${process.env.VUE_APP_CLIENT_URL}/frisky-social-share-image.png`
        const canonicalUrl = `https://frisky.fm${this.$route.path}`
        return {
            title,
            link: [{ rel: 'canonical', vmid: 'canonical', href: canonicalUrl }],
            meta: [
                { name: 'title', vmid: 'title', content: title },
                { name: 'description', vmid: 'description', content: description },

                { property: 'og:title', vmid: 'og:title', content: title },
                { property: 'og:description', vmid: 'og:description', content: description },
                { property: 'og:image', vmid: 'og:image', content: image },
                { property: 'og:type', vmid: 'og:type', content: 'music.playlist' },
                { property: 'og:url', vmid: 'og:url', content: canonicalUrl },
                { property: 'og:site_name', vmid: 'og:site_name', content: 'FRISKY' },

                { name: 'twitter:card', vmid: 'twitter:card', content: 'summary_large_image' },
                { name: 'twitter:title', vmid: 'twitter:title', content: title },
                { name: 'twitter:description', vmid: 'twitter:description', content: description },
                { name: 'twitter:image', vmid: 'twitter:image', content: image },
                { name: 'twitter:url', vmid: 'twitter:url', content: canonicalUrl },
            ],
        }
    },
    components: { Layout, PlaylistsTableBlock, PlaylistInfo },
    data() {
        return {
            chartMixes: Array.from(Array(20), (e, i) => ({
                id: Number.MAX_SAFE_INTEGER - i,
                genre: [],
            })),
            shouldTransition: false,
            maxMixes: 20,
            increaseMixes: 20,
            currentChart: null,
            totalMixes: null,
            shouldLoadMore: false,
            loaded: false,
        }
    },
    computed: {
        id() {
            return this.$route.params.id
        },
        scroll() {
            return this.$store.state.appearance.scroll
        },
        scrollChild() {
            return this.$store.state.appearance.scrollChild
        },
        isCurrentChartPlaying() {
            return Number(this.id) === this.$store.state.queue.playlistId
        },
        hasReachedMaxMixes() {
            return this.maxMixes >= Number(this.totalMixes)
        },
        playback() {
            return this.$store.state.playback
        },
        loggedIn() {
            return this.$store.getters['auth/loggedIn']
        },
        currentPosition() {
            return this.$store.state.queue.position
        },
    },
    watch: {
        scroll() {
            if (!this.hasReachedMaxMixes) {
                this.shouldLoadMore = true
                const bottomOfWindow =
                    this.scroll + window.innerHeight >=
                    this.$store.state.appearance.scrollChild.offsetHeight - window.innerHeight / 3
                if (bottomOfWindow && this.shouldLoadMore && this.loaded) {
                    this.shouldLoadMore = false
                    setTimeout(() => {
                        this.shouldLoadMore = true
                    }, 200)
                    this.loadMoreMixes()
                }
            }
        },
    },
    mounted() {
        DataLayer.playlists.getById(this.id).then(res => {
            this.currentChart = res.body
        })
        DataLayer.playlists.getRevisionsMixes(this.id, this.increaseMixes, 0).then(res => {
            this.loaded = true
            this.totalMixes = Number(res.headers['x-items-total'])
            this.chartMixes = res.body.map(revisionsMixes => revisionsMixes.mixes)
        })
    },
    methods: {
        loadMoreMixes(fillAll = false, play) {
            this.loaded = false
            this.maxMixes += this.increaseMixes
            const increaseMixes = fillAll ? this.totalMixes - this.chartMixes.length : this.increaseMixes

            return DataLayer.playlists.getRevisionsMixes(this.id, increaseMixes, this.chartMixes.length).then(mixes => {
                this.loaded = true
                return this.chartMixes.push(...mixes.body.map(revisionsMixes => revisionsMixes.mixes))
            })
        },
        playPlaylist(position) {
            if (!this.loggedIn) {
                modalService.show('AUTH_REQUIRED_FOR_PLAYBACK')
                return
            }
            if (this.chartMixes.length === this.totalMixes) {
                this.$queueService.playMixMultiple({
                    mixes: [...this.chartMixes],
                    id: this.currentChart.id,
                    position,
                })
            } else {
                this.loadMoreMixes(true).then(mixes => {
                    this.$queueService.playMixMultiple({
                        mixes: [...this.chartMixes],
                        id: this.currentChart.id,
                        position,
                    })
                })
            }
        },
    },
}
</script>

<template>
    <Layout>
        <BaseContent>
            <BaseSection
                :class="$style.container"
                padded
            >
                <h6 :class="$style.title">Chart</h6>
                <div :class="$style.content">
                    <PlaylistInfo
                        :class="$style.details"
                        :playlist-mixes="chartMixes"
                        :playlist="currentChart"
                        @playPlaylist="playPlaylist(currentPosition !== -1 ? currentPosition : 0)"
                    />
                    <PlaylistsTableBlock
                        :mixes="chartMixes"
                        :error="{
                            title: `There are no mixes in this chart yet`,
                        }"
                        :should-transition="shouldTransition"
                        :class="$style.list"
                        playlist-id
                        genres
                        is-indexed-list
                        @playPlaylistMix="playPlaylist"
                    />
                </div>
            </BaseSection>
        </BaseContent>
    </Layout>
</template>

<style lang="scss" module>
@import '@design';

.container {
    margin-top: $s-h;
}

.content {
    @include widescreen {
        display: grid;
        grid-template-columns: 1fr 3fr;
        gap: $s-xxxl;
    }
}

.details {
    max-width: 300px;
    margin: 0 auto;
}

.title {
    margin-bottom: $s-xl;
}
</style>
